import { fetchApi } from '@/services/api-client';
import type { EditorJsContent } from '@/services/editor-js';

export const NEWS_PATH = '/news';

export type NewsCategoryRef = {
  id: string;
  name: string;
  color: string;
};

export type NewsCategory = {
  id: string;
  name: string;
  color: string;
  articleCount: number;
};

export type News = {
  id: string;
  title: string;
  slug: string;
  excerpt: string;
  content: string;
  editorContent?: EditorJsContent;
  category: NewsCategoryRef | null;
  author: string;
  authorRole: string;
  imageUrl: string;
  metaTitle?: string;
  metaDescription?: string;
  metaImageUrl?: string;
  publishedAt: string;
  readTime: number;
  featured: boolean;
  published: boolean;
  scheduledFor: string | null;
};

export type CreateNewsData = {
  title: string;
  slug?: string;
  excerpt: string;
  content: string;
  editorContent?: EditorJsContent;
  categoryId: string;
  author: string;
  authorRole: string;
  imageUrl?: string;
  metaTitle?: string;
  metaDescription?: string;
  metaImageUrl?: string;
  readTime?: number;
  featured?: boolean;
  published?: boolean;
  scheduledFor?: string | null;
};

export type UpdateNewsData = Partial<CreateNewsData>;

type NewsListResponse = {
  data: News[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
};

export const newsApi = {
  getAll: (page = 1, limit = 10, categoryId?: string) => {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });
    if (categoryId) params.append('categoryId', categoryId);
    return fetchApi<NewsListResponse>(`${NEWS_PATH}/admin?${params}`);
  },
  getById: (id: string) => fetchApi<News>(`${NEWS_PATH}/admin/by-id/${id}`),
  create: (data: CreateNewsData) => fetchApi<News>(NEWS_PATH, { method: 'POST', body: JSON.stringify(data) }),
  update: (id: string, data: UpdateNewsData) =>
    fetchApi<News>(`${NEWS_PATH}/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (id: string) => fetchApi<{ success: boolean }>(`${NEWS_PATH}/${id}`, { method: 'DELETE' }),
  togglePublished: (id: string) => fetchApi<News>(`${NEWS_PATH}/${id}/publish`, { method: 'PATCH' }),
  getCategories: () => fetchApi<NewsCategoryRef[]>(`${NEWS_PATH}/categories`),
  getManagedCategories: () => fetchApi<NewsCategory[]>(`${NEWS_PATH}/admin/categories`),
  createCategory: (data: { name: string; color?: string }) =>
    fetchApi<NewsCategory>(`${NEWS_PATH}/admin/categories`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  updateCategory: (id: string, data: { name: string; color?: string }) =>
    fetchApi<NewsCategory>(`${NEWS_PATH}/admin/categories/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
  deleteCategory: (id: string) =>
    fetchApi<{ success: boolean }>(`${NEWS_PATH}/admin/categories/${id}`, { method: 'DELETE' }),
};
